import React, { useState, useEffect } from 'react';
import { Check, Star, ArrowRight, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { PackName } from './ui/PackName';
import { packs } from '../data/mock';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = window.location.origin;

const PackCard = ({ pack, onSelect, outOfStock }) => {
  const handleClick = () => {
    if (!outOfStock) onSelect(pack);
  };

  return (
    <div
      onClick={handleClick}
      className={`relative bg-white rounded-3xl overflow-hidden transition-all duration-500 h-full flex flex-col ${
        outOfStock
          ? 'opacity-75 grayscale-[30%]'
          : 'hover:shadow-2xl hover:-translate-y-2 cursor-pointer'
      } ${
        pack.popular && !outOfStock
          ? 'ring-2 ring-[#DFFF00] shadow-xl shadow-[#DFFF00]/10'
          : 'shadow-lg hover:shadow-slate-200/50'
      }`}
      data-testid={`pack-card-${pack.id}`}
    >
      {/* Popular Badge */}
      {pack.popular && !outOfStock && (
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10">
          <Badge className="bg-[#DFFF00] text-black font-semibold px-2 py-0.5 sm:px-3 sm:py-1 text-xs sm:text-sm">
            <Star className="w-3 h-3 mr-1 fill-current" />
            <span className="hidden sm:inline">Le + populaire</span>
            <span className="sm:hidden">Populaire</span>
          </Badge>
        </div>
      )}

      {/* Out of Stock Badge */}
      {outOfStock && (
        <div className="absolute top-3 right-3 sm:top-4 sm:right-4 z-10">
          <Badge data-testid={`out-of-stock-badge-${pack.id}`} className="bg-red-500 text-white font-semibold px-2 py-0.5 sm:px-3 sm:py-1 text-xs sm:text-sm">
            <AlertTriangle className="w-3 h-3 mr-1" />
            Rupture de stock
          </Badge>
        </div>
      )}

      {/* Image */}
      <div className="relative h-40 sm:h-48 md:h-52 lg:h-56 overflow-hidden bg-slate-100">
        <img
          src={pack.image}
          alt={pack.name}
          className="w-full h-full object-cover object-center transform hover:scale-105 transition-transform duration-500"
        />
        {pack.bonus && (
          <div className="absolute bottom-2 left-2 sm:bottom-4 sm:left-4 bg-emerald-500 text-white text-[10px] sm:text-xs font-bold px-2 py-1 sm:px-3 sm:py-1.5 rounded-full">
            {pack.bonus}
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 sm:p-6 flex flex-col flex-grow">
        <div className="flex items-start justify-between mb-3 sm:mb-4">
          <div>
            <h3 className="text-lg sm:text-xl font-bold text-slate-900 mb-1"><PackName name={pack.name} /></h3>
            <div className="flex items-center gap-1 sm:gap-2">
              <span className="text-xl sm:text-2xl font-bold text-[#9aae00]">{pack.surface}</span>
              {pack.dimensions && (
                <span className="text-xs sm:text-sm text-slate-500">({pack.dimensions})</span>
              )}
            </div>
          </div>
        </div>

        <p className="text-slate-600 text-xs sm:text-sm mb-3 sm:mb-4 line-clamp-2">{pack.description}</p>

        {/* Price */}
        <div className="bg-slate-50 rounded-xl sm:rounded-2xl p-3 sm:p-4 mb-3 sm:mb-4">
          <div className="flex items-baseline gap-1">
            <span className="text-2xl sm:text-4xl font-bold text-slate-900">{pack.price}€</span>
            <span className="text-slate-500 text-sm sm:text-base">/mois</span>
          </div>
          <p className="text-[10px] sm:text-xs text-slate-400 mt-1">TTC • Sans engagement après 1 mois</p>
        </div>

        {/* Usages */}
        <div className="mb-3 sm:mb-4 hidden sm:block">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Usages</p>
          <div className="flex flex-wrap gap-1 sm:gap-2">
            {pack.usages.slice(0, 3).map((usage) => (
              <span
                key={usage}
                className="text-[10px] sm:text-xs bg-slate-100 text-slate-600 px-2 py-0.5 sm:py-1 rounded-full"
              >
                {usage}
              </span>
            ))}
          </div>
        </div>

        {/* Includes */}
        <div className="border-t border-slate-100 pt-3 sm:pt-4 mb-4 sm:mb-6 flex-grow">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 sm:mb-3">Inclus</p>
          <ul className="space-y-1 sm:space-y-2">
            {pack.includes.slice(0, 5).map((item) => (
              <li key={item} className="flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                <Check className="w-3 h-3 sm:w-4 sm:h-4 text-[#9aae00] flex-shrink-0" />
                <span className="truncate">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA */}
        <div className="mt-auto">
          {outOfStock ? (
            <Button
              disabled
              data-testid={`reserve-btn-disabled-${pack.id}`}
              className="w-full py-4 sm:py-6 rounded-xl font-semibold text-sm sm:text-base bg-slate-300 text-slate-500 cursor-not-allowed"
            >
              Rupture de stock
            </Button>
          ) : (
            <Button
              onClick={() => onSelect(pack)}
              data-testid={`reserve-btn-${pack.id}`}
              className={`w-full py-4 sm:py-6 rounded-xl font-semibold transition-all group text-sm sm:text-base ${
                pack.popular
                  ? 'bg-[#DFFF00] hover:bg-[#c8e600] text-black shadow-lg shadow-[#DFFF00]/25'
                  : 'bg-black hover:bg-slate-800 text-white'
              }`}
            >
              Réserver ce pack
              <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

const PacksSection = () => {
  const navigate = useNavigate();
  const [availability, setAvailability] = useState({});

  useEffect(() => {
    const fetchAvailability = async () => {
      try {
        const res = await axios.get(`${API}/api/packs/availability`);
        setAvailability(res.data);
      } catch (err) {
        console.error('Failed to fetch availability:', err);
      }
    };
    fetchAvailability();
  }, []);

  const handleSelectPack = (pack) => {
    navigate('/reservation', { state: { selectedPack: pack } });
  };

  return (
    <section id="packs" className="py-12 sm:py-16 lg:py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <span className="inline-block text-[#9aae00] font-semibold text-xs sm:text-sm uppercase tracking-wider mb-3 sm:mb-4">
            Nos offres
          </span>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl xl:text-5xl font-bold text-slate-900 mb-3 sm:mb-4">
            Des packs clairs et prêts à l'emploi
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-slate-600 max-w-2xl mx-auto px-4">
            Choisissez le format adapté à votre événement. Tout est inclus, livré partout en France.
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          {packs.map((pack) => {
            const packAvail = availability[String(pack.id)];
            const outOfStock = packAvail && packAvail.available === false;
            return (
              <PackCard key={pack.id} pack={pack} onSelect={handleSelectPack} outOfStock={outOfStock} />
            );
          })}
        </div>

        <div className="mt-8 sm:mt-12 text-center">
          <p className="text-sm sm:text-base text-slate-500">
            Besoin d'une configuration personnalisée ?{' '}
            <a href="/contact" className="text-[#9aae00] font-semibold hover:underline">
              Contactez-nous
            </a>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PacksSection;
